/**
 * affiliate.ts — Kiểu dữ liệu + hàm thuần cho Tiếp thị liên kết (app đối tác, trang /r/:mã, Platform).
 * Backend: ecdp_backend_source/src/modules/affiliate/*.
 */
import { i18n } from '@/lib/i18n'

// ── Kiểu dữ liệu (khớp API) ────────────────────────────────────────────

export type CommissionType = 'percent' | 'fixed'
export type CommissionScope = 'first_order' | 'recurring'
export type AttributionModel = 'first_touch' | 'last_touch'
export type DiscountMode = 'off' | 'percent' | 'fixed'

export interface CommissionTier {
  name: string
  minRevenue: number
  minPayingCustomers: number
  value: number
}

export interface AffiliateConfig {
  version: number
  enabled: boolean
  commission: {
    type: CommissionType
    percent: number
    fixedAmount: number
    scope: CommissionScope
    durationDays: number
    tiers: CommissionTier[]
    excludeProviders: string[]
    /** Trần tổng hoa hồng mỗi khách (VND); null = không giới hạn. */
    capPerCustomer: number | null
  }
  holdDays: number
  cookieDays: number
  bindWindowDays: number
  attributionModel: AttributionModel
  minWithdrawal: number
  customerDiscount: { mode: DiscountMode; value: number; maxDiscountAmount: number | null; purchases: number }
  tax: {
    mode: 'withhold_individual' | 'none'
    ratePercent: number
    thresholdPerPayment: number
    allowCommitment08: boolean
    companyRequiresInvoice: boolean
  }
  vat: { source: 'einvoice' | 'manual'; includesVat: boolean; ratePercent: number }
  terms: string
  /** Bên chi trả in trên chứng từ thuế TNCN (trống → tên thương hiệu). */
  payer: { name: string; taxCode: string; address: string }
}

export type PublicProgram = Omit<AffiliateConfig, 'version' | 'vat' | 'commission' | 'payer'> & {
  commission: Omit<AffiliateConfig['commission'], 'excludeProviders'>
}

export interface PartnerView {
  id: string
  code: string
  status: 'active' | 'suspended'
  statusReason: string | null
  partnerType: 'individual' | 'company'
  fullName: string
  phone: string
  email: string
  address: string
  companyName: string | null
  taxCode: string | null
  idNumberMasked: string
  bankName: string
  bankAccountMasked: string
  bankAccountHolder: string
  bankBranch: string | null
  pitCommitment: boolean
  payoutInfoChangedAt: string | null
  codeChangedAt: string | null
  codeChangeCount: number
  hasPromoCode: boolean
  createdAt: string
}

export interface CommissionItem {
  id: string
  kind: 'commission' | 'adjustment'
  customer: string | null
  orderId: string | null
  baseAmount: number
  rateType: CommissionType | null
  rateValue: number | null
  amount: number
  status: 'holding' | 'available' | 'void'
  orderPaidAt: string | null
  holdUntil: string | null
  availableAt: string | null
  reason: string | null
  voidReason: string | null
  voidedAt: string | null
  createdAt: string
  tier: string | null
}

export interface WithdrawalItem {
  id: string
  code: string
  amount: number
  taxWithheld: number
  netAmount: number
  taxRule: TaxRule
  status: WithdrawalStatus
  payoutMasked: string
  requestedAt: string
  reviewedAt: string | null
  rejectReason: string | null
  exportedAt: string | null
  paidAt: string | null
  transferRef: string | null
}

export type WithdrawalStatus = 'pending' | 'approved' | 'paid' | 'rejected' | 'cancelled'
export type TaxRule = 'disabled' | 'company_invoice' | 'commitment_08' | 'below_threshold' | 'withhold'

export interface TaxPreview {
  rule: TaxRule
  ratePercent: number
  taxWithheld: number
  net: number
  label: string
}

// ── Nhãn (hàm — đọc i18n.t() lúc dùng, không đóng băng theo ngôn ngữ) ───

export function commissionStatusLabel(status: CommissionItem['status']): string {
  return i18n.t(`sharedMisc:affiliate.commissionStatus.${status}`)
}

export function withdrawalStatusLabel(status: WithdrawalStatus): string {
  return i18n.t(`sharedMisc:affiliate.withdrawalStatus.${status}`)
}

export const WITHDRAWAL_STATUS_TONE: Record<WithdrawalStatus, 'warning' | 'default' | 'success' | 'destructive' | 'secondary'> = {
  pending: 'warning',
  approved: 'default',
  paid: 'success',
  rejected: 'destructive',
  cancelled: 'secondary',
}

export function referralSourceLabel(source: string): string {
  const key = `sharedMisc:affiliate.referralSource.${source}`
  return i18n.exists(key) ? i18n.t(key) : source
}

const TAX_RULE_KEY: Record<TaxRule, string> = {
  disabled: 'disabled',
  company_invoice: 'companyInvoice',
  commitment_08: 'commitment08',
  below_threshold: 'belowThreshold',
  withhold: 'withhold',
}

export function taxRuleLabel(rule: TaxRule): string {
  return i18n.t(`sharedMisc:affiliate.taxRule.${TAX_RULE_KEY[rule]}`)
}

const vnd = (n: number) => `${Math.round(n).toLocaleString('vi-VN')}đ`

/** "20% giá trị đơn (trước VAT)" / "200.000đ mỗi đơn". */
export function rateText(type: CommissionType, value: number): string {
  return type === 'percent'
    ? i18n.t('sharedMisc:affiliate.rateText.percent', { value: value.toLocaleString('vi-VN') })
    : i18n.t('sharedMisc:affiliate.rateText.fixed', { amount: vnd(value) })
}

/** Câu mô tả quy tắc hoa hồng cho đối tác. */
export function programSummary(p: Pick<PublicProgram, 'commission' | 'holdDays' | 'minWithdrawal'>): string {
  const c = p.commission
  const base = rateText(c.type, c.type === 'percent' ? c.percent : c.fixedAmount)
  const scope = i18n.t(c.scope === 'first_order' ? 'sharedMisc:affiliate.summary.scopeFirst' : 'sharedMisc:affiliate.summary.scopeAll')
  const window = c.durationDays > 0 ? i18n.t('sharedMisc:affiliate.summary.window', { days: c.durationDays }) : ''
  return i18n.t('sharedMisc:affiliate.summary.main', {
    base, scope, window, holdDays: p.holdDays, minWithdrawal: vnd(p.minWithdrawal),
  })
}

export function tierText(t: CommissionTier, type: CommissionType): string {
  const cond = [
    t.minRevenue > 0 && i18n.t('sharedMisc:affiliate.tier.revenueCond', { amount: vnd(t.minRevenue) }),
    t.minPayingCustomers > 0 && i18n.t('sharedMisc:affiliate.tier.customersCond', { count: t.minPayingCustomers }),
  ].filter(Boolean).join(` ${i18n.t('sharedMisc:affiliate.tier.and')} `)
  return i18n.t('sharedMisc:affiliate.tier.text', { name: t.name, cond, rate: rateText(type, t.value) })
}

export function discountText(d: { mode: DiscountMode; value: number; maxDiscountAmount: number | null; purchases: number }): string | null {
  if (d.mode === 'off') return null
  const v = d.mode === 'percent'
    ? i18n.t('sharedMisc:affiliate.discount.percentText', { value: d.value }) +
      (d.maxDiscountAmount ? i18n.t('sharedMisc:affiliate.discount.percentMax', { amount: vnd(d.maxDiscountAmount) }) : '')
    : i18n.t('sharedMisc:affiliate.discount.fixedText', { amount: vnd(d.value) })
  const purchases = d.purchases === 1
    ? i18n.t('sharedMisc:affiliate.discount.firstPurchase')
    : i18n.t('sharedMisc:affiliate.discount.nPurchases', { count: d.purchases })
  return `${v} ${purchases}`
}

// ── Link / mã ───────────────────────────────────────────────────────────

export const AFFILIATE_CODE_RE = /^[A-Z0-9][A-Z0-9-]{3,19}$/

export function normalizeCode(raw: string): string {
  return raw.trim().toUpperCase()
}

export function codeError(raw: string): string | null {
  const c = normalizeCode(raw)
  if (!AFFILIATE_CODE_RE.test(c)) return i18n.t('sharedMisc:affiliate.codeError')
  return null
}

/** Link giới thiệu trên tên miền hệ thống. */
export function referralLink(origin: string, code: string): string {
  return `${origin.replace(/\/$/, '')}/r/${encodeURIComponent(code)}`
}

// ── Vé giới thiệu trên trình duyệt (trang /r/:mã) ───────────────────────

export const REF_STORAGE_KEY = 'ecdp_ref'
export const VISITOR_STORAGE_KEY = 'ecdp_vid'

export interface StoredRef {
  code: string
  ticket: string
  savedAt: number
  expiresAt: number
}

export function parseStoredRef(raw: string | null, now: number): StoredRef | null {
  if (!raw) return null
  try {
    const r = JSON.parse(raw) as StoredRef
    if (typeof r.code !== 'string' || typeof r.ticket !== 'string' || typeof r.expiresAt !== 'number') return null
    return r.expiresAt > now ? r : null
  } catch {
    return null
  }
}

/**
 * Có ghi vé mới không: first-touch giữ vé cũ còn hạn (trừ khi cùng mã → làm mới), last-touch luôn thay.
 */
export function shouldReplaceRef(existing: StoredRef | null, newCode: string, model: AttributionModel): boolean {
  if (!existing) return true
  if (model === 'last_touch') return true
  return existing.code === newCode
}

export function makeStoredRef(code: string, ticket: string, cookieDays: number, now: number): StoredRef {
  return { code, ticket, savedAt: now, expiresAt: now + cookieDays * 24 * 60 * 60 * 1000 }
}

// ── Form đăng ký ────────────────────────────────────────────────────────

export interface RegisterForm {
  partnerType: 'individual' | 'company'
  fullName: string
  phone: string
  email: string
  address: string
  companyName: string
  taxCode: string
  idNumber: string
  bankName: string
  bankAccount: string
  bankAccountHolder: string
  bankBranch: string
  pitCommitment: boolean
  code: string
  acceptTerms: boolean
}

export const EMPTY_REGISTER_FORM: RegisterForm = {
  partnerType: 'individual', fullName: '', phone: '', email: '', address: '', companyName: '', taxCode: '', idNumber: '',
  bankName: '', bankAccount: '', bankAccountHolder: '', bankBranch: '', pitCommitment: false, code: '', acceptTerms: false,
}

/** Kiểm tra phía giao diện (server kiểm lại). Trả { trường: lỗi }. */
export function validateRegisterForm(f: RegisterForm, opts: { requirePayout?: boolean; requireTerms?: boolean } = {}): Partial<Record<keyof RegisterForm, string>> {
  const e: Partial<Record<keyof RegisterForm, string>> = {}
  const requirePayout = opts.requirePayout ?? true
  if (f.fullName.trim().length < 2) e.fullName = i18n.t('sharedMisc:affiliate.form.fullNameRequired')
  const digits = f.phone.replace(/\D/g, '')
  if (!/^(0\d{9}|84\d{9})$/.test(digits)) e.phone = i18n.t('sharedMisc:affiliate.form.phoneInvalid')
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(f.email.trim())) e.email = i18n.t('sharedMisc:affiliate.form.emailInvalid')
  if (f.address.trim().length < 5) e.address = i18n.t('sharedMisc:affiliate.form.addressRequired')
  if (f.partnerType === 'company') {
    if (f.companyName.trim().length < 2) e.companyName = i18n.t('sharedMisc:affiliate.form.companyNameRequired')
    if (!/^\d{10}(-\d{3})?$/.test(f.taxCode.replace(/\s/g, ''))) e.taxCode = i18n.t('sharedMisc:affiliate.form.taxCodeInvalid')
  }
  if (requirePayout) {
    if (f.partnerType === 'individual' && !/^\d{12}$/.test(f.idNumber.replace(/\s/g, ''))) e.idNumber = i18n.t('sharedMisc:affiliate.form.idNumberInvalid')
    if (f.bankName.trim().length < 2) e.bankName = i18n.t('sharedMisc:affiliate.form.bankNameRequired')
    if (!/^\d{6,20}$/.test(f.bankAccount.replace(/[\s.-]/g, ''))) e.bankAccount = i18n.t('sharedMisc:affiliate.form.bankAccountInvalid')
    if (f.bankAccountHolder.trim().length < 2) e.bankAccountHolder = i18n.t('sharedMisc:affiliate.form.bankAccountHolderRequired')
  }
  if (f.code.trim()) {
    const ce = codeError(f.code)
    if (ce) e.code = ce
  }
  if ((opts.requireTerms ?? true) && !f.acceptTerms) e.acceptTerms = i18n.t('sharedMisc:affiliate.form.acceptTermsRequired')
  return e
}

// ── Cấu hình (Platform) ─────────────────────────────────────────────────

/** Kiểm tra nhanh trước khi lưu (server kiểm đầy đủ). */
export function validateConfig(c: AffiliateConfig): string | null {
  if (c.commission.type === 'percent' && (c.commission.percent < 0 || c.commission.percent > 100)) return i18n.t('sharedMisc:affiliate.config.percentRange')
  if (c.commission.type === 'fixed' && c.commission.fixedAmount < 0) return i18n.t('sharedMisc:affiliate.config.fixedInvalid')
  for (const [i, t] of c.commission.tiers.entries()) {
    if (!t.minRevenue && !t.minPayingCustomers) return i18n.t('sharedMisc:affiliate.config.tierNeedCondition', { n: i + 1 })
    if (c.commission.type === 'percent' && (t.value < 0 || t.value > 100)) return i18n.t('sharedMisc:affiliate.config.tierPercentRange', { n: i + 1 })
  }
  if (c.cookieDays < 1) return i18n.t('sharedMisc:affiliate.config.cookieDaysMin')
  if (c.customerDiscount.mode === 'percent' && (c.customerDiscount.value < 1 || c.customerDiscount.value > 100)) return i18n.t('sharedMisc:affiliate.config.discountPercentRange')
  if (c.customerDiscount.mode === 'fixed' && c.customerDiscount.value < 1000) return i18n.t('sharedMisc:affiliate.config.discountFixedMin')
  if (c.tax.ratePercent < 0 || c.tax.ratePercent > 50) return i18n.t('sharedMisc:affiliate.config.taxRateInvalid')
  return null
}

/** Sắp bậc tăng dần theo điều kiện để dễ đọc. */
export function sortTiers(tiers: CommissionTier[]): CommissionTier[] {
  return [...tiers].sort((a, b) => a.minRevenue - b.minRevenue || a.minPayingCustomers - b.minPayingCustomers)
}

/** Tiền VND có dấu (điều chỉnh âm / số dư âm) — formatVnd chung kẹp về 0 nên không dùng được ở đây. */
export function formatSignedVnd(n: number): string {
  const v = Math.round(Number(n) || 0)
  return `${v < 0 ? '−' : ''}${new Intl.NumberFormat('vi-VN').format(Math.abs(v))} ₫`
}

/** Ước tính thực nhận khi rút (giao diện; server tính chính thức). */
export function estimateNet(amount: number, partner: { partnerType: string; pitCommitment: boolean }, tax: AffiliateConfig['tax']): TaxPreview {
  const mk = (rule: TaxRule, ratePercent: number, taxWithheld: number): TaxPreview =>
    ({ rule, ratePercent, taxWithheld, net: amount - taxWithheld, label: taxRuleLabel(rule) })
  if (tax.mode === 'none') return mk('disabled', 0, 0)
  if (partner.partnerType === 'company') return mk('company_invoice', 0, 0)
  if (partner.pitCommitment && tax.allowCommitment08) return mk('commitment_08', 0, 0)
  if (amount < tax.thresholdPerPayment) return mk('below_threshold', 0, 0)
  return mk('withhold', tax.ratePercent, Math.round((amount * tax.ratePercent) / 100))
}

// ── Tắt chương trình / đối tác bị khoá (E2, E22) ───────────────────────

export function suspendedBanner(): string {
  return i18n.t('sharedMisc:affiliate.suspendedBanner')
}
export function programClosedText(): string {
  return i18n.t('sharedMisc:affiliate.programClosedText')
}

/**
 * Hiện mục "Tiếp thị liên kết" trong menu? Tắt chương trình chỉ chặn đăng ký mới → đối tác cũ vẫn thấy; người chưa là
 * đối tác thì ẩn. Chưa tải xong / lỗi → hiện (không giấu nhầm của đối tác).
 */
export function showAffiliateMenu(me: { program: { enabled: boolean }; partner: unknown | null } | undefined): boolean {
  if (!me) return true
  return me.program.enabled || !!me.partner
}

// ── Kỳ báo cáo thuế TNCN (E14) ─────────────────────────────────────────

export type PeriodKind = 'month' | 'quarter' | 'year'
export interface PeriodSel {
  kind: PeriodKind
  year: number
  month: number
  quarter: number
}

export function defaultPeriod(now = new Date()): PeriodSel {
  return { kind: 'year', year: now.getFullYear(), month: now.getMonth() + 1, quarter: Math.floor(now.getMonth() / 3) + 1 }
}

/** Tham số query cho API (?period&year&month|quarter). */
export function periodParams(p: PeriodSel): Record<string, string> {
  const q: Record<string, string> = { period: p.kind, year: String(p.year) }
  if (p.kind === 'month') q.month = String(p.month)
  if (p.kind === 'quarter') q.quarter = String(p.quarter)
  return q
}

export function periodLabel(p: PeriodSel): string {
  if (p.kind === 'month') return i18n.t('sharedMisc:affiliate.period.month', { month: String(p.month).padStart(2, '0'), year: p.year })
  if (p.kind === 'quarter') return i18n.t('sharedMisc:affiliate.period.quarter', { quarter: p.quarter, year: p.year })
  return i18n.t('sharedMisc:affiliate.period.year', { year: p.year })
}

// ── Xuất lại lệnh đã xuất (E17) ────────────────────────────────────────

export interface ExportedInfo { id: string; code: string; batch: string | null }

/** Câu hỏi xác nhận xuất lại: liệt kê "đã xuất ở lô X". */
export function reexportConfirmText(list: ExportedInfo[]): string {
  const lines = list.slice(0, 10).map((x) => i18n.t('sharedMisc:affiliate.reexport.line', { code: x.code, batch: x.batch ?? '?' }))
  if (list.length > 10) lines.push(i18n.t('sharedMisc:affiliate.reexport.more', { count: list.length - 10 }))
  return i18n.t('sharedMisc:affiliate.reexport.confirm', { lines: lines.join('\n') })
}
